import { showToast } from "src/components/ToastProvider";
import ToastTypes from "src/enum/ToastTypes";
import {
  applyCouponApi,
  fetchSupportedPaymentGateways,
  removeCouponApi,
  updateOrderPaymentMethodApi,
} from "src/services/CheckoutApi";
import {
  addLineItemToOrderApi,
  deletelineitemApi,
} from "src/services/LineItemApi";
import {
  createOrder,
  fetchOrders,
  placeOrderApi,
  updateCustomerInfoOrderApi,
} from "src/services/OrderApi";
import { fetchAndSetOrders } from "./orderHelper";
import { addLineitemError, clearLineitemErrors } from "src/store/slices/lineitemErrors-slice";
import { setnavigateCheckout } from "src/store/slices/navigateCheckout-slice";
import { store } from "src/store/store";

// Maps the gateway names returned by /supportedpaymentgateways onto the API's
// PaymentMethod enum. Kept in one place because the mapping is needed both when
// the buyer picks a method and when a default is applied — they previously
// duplicated this, which made it easy to add a gateway to only one of them.
const PAYMENT_METHOD_NUMBERS = {
  Razorpay: 1,
  UPI: 2,
  COD: 3,
  Cashfree: 4,
  Stripe: 5,
  PayPal: 6,
  PayMongo: 7,
};

// Returns null for a gateway we don't know. Callers must bail rather than
// default — persisting the wrong number routes the buyer to a processor the
// seller never picked.
const paymentMethodNumber = (method) => PAYMENT_METHOD_NUMBERS[method] ?? null;

export const loadPaymentMethods = async (setPaymentMethods) => {
  try {
    const methods = await fetchSupportedPaymentGateways();
    setPaymentMethods(methods);
  } catch (error) {
    console.error("Failed to load payment methods:", error);
  }
};

export const checkoutAndAddLineItem = async (cartItems, dispatch) => {
  
  try {
    const prevOrders = await fetchOrders();
    
    if (prevOrders.length === 0) {
      await createOrder();
    }
    let orders = await fetchOrders();
    
    const orderStatus = orders[0].orderStatus;
    
    if (orderStatus !== 9) {
      await createOrder();
    }
    orders = await fetchOrders();
    
    const lineItems = orders[0].lineItems;
    
    if (orders[0]?.discountAmount) {
      await removeCouponApi(orders[0]);
    }
    for (let i = 0; i < lineItems.length; i++) {
      let item = lineItems[i];
      await deletelineitemApi(item.ID);
    }

    // Clear previous lineitem errors
    if (dispatch) {
      dispatch(clearLineitemErrors());
    }

    // Collect lineitem responses with error handling
    const lineItemResponses = [];
    const failedProducts = [];
    const successfulProducts = [];
    
    for (let i = 0; i < cartItems.length; i++) {
      let item = cartItems[i];
      
      const response = await addLineItemToOrderApi(
        orders[0].ID,
        item.productId || item.ID,
        item.quantity,
        item.variantId,
        item.slotId
      );
      
      lineItemResponses.push({
        cartItem: item,
        response: response
      });

      // Store error in Redux if there's an error
      if (dispatch && !response.success && response.error) {
        const productId = item.productId || item.ID;
        dispatch(addLineitemError({
          productId: productId,
          error: response.error,
          lineitem: response.data
        }));
        
        // Also save failed product for display
        failedProducts.push({
          ...item,
          error: response.error,
          lineitem: response.data
        });
      } else {
        // Track successful products for cart clearing
        successfulProducts.push(item);
      }
    }
    
    // Don't clear cart during checkout - only clear when order is actually placed
    // The cart will be cleared in placeOrderApi when the order is successfully placed
    
    // Return both the updated orders and the lineitem responses for error handling
    const updatedOrders = await fetchOrders();
    
    return {
      orders: updatedOrders,
      lineItemResponses: lineItemResponses,
      failedProducts: failedProducts
    };
  } catch (error) {
    console.error("Failed to add line items:", error);
    return {
      orders: null,
      lineItemResponses: [],
      failedProducts: [],
      error: error
    };
  }
};

export const addCouponClicked = async (discountCode, orders, setOrders) => {
  const trimmedCode = discountCode.trim();
  const currentOrderId = orders?.[0]?.ID;

  if (!trimmedCode) {
    showToast("Please enter a coupon code.", ToastTypes.ERROR);
    return;
  }
  const result = await applyCouponApi(trimmedCode, currentOrderId);
  if (result?.status === 1) {
    showToast(result?.message, ToastTypes.ERROR);
    return;
  }
  if (result?.data?.status === 1) {
    showToast(
      "This coupon is invalid or cannot be applied to your order." ||
        result.data.message,
      ToastTypes.ERROR
    );
    return;
  }
  showToast("Coupon applied successfully!", ToastTypes.SUCCESS);
  await fetchAndSetOrders(setOrders);
};

export const removeCouponClicked = async (
  setDiscountCode,
  setDiscountAmount,
  orders,
  setOrders
) => {
  setDiscountCode("");
  setDiscountAmount(0);
  await removeCouponApi(orders[0]);
  await fetchAndSetOrders(setOrders);
  showToast("Coupon removed successfully.", ToastTypes.SUCCESS);
};

export const handlePlaceOrderClicked = async (
  personalInfo,
  setPlacingOrder,
  navigate,
  orders,
  selectedAddressId,
  dispatch,
  setEmail,
  paymentMethod,
  setShowUpiModal
) => {
  if (!personalInfo.firstName.trim()) {
    showToast("Please enter your first name", ToastTypes.ERROR);
    return;
  }
  if (!personalInfo.lastName.trim()) {
    showToast("Please enter your last name", ToastTypes.ERROR);
    return;
  }

  if (!personalInfo.email) {
    showToast("Please enter your email address", ToastTypes.ERROR);
    return;
  }

  // Basic email validation with regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(personalInfo.email)) {
    showToast("Please enter a valid email address", ToastTypes.ERROR);
    return;
  }

  if (!selectedAddressId) {
    showToast("Please create an address", ToastTypes.ERROR);
    return;
  }

  if (dispatch && setEmail) {
    dispatch(setEmail(personalInfo.email));
  }

  // If payment method is UPI, show the confirmation modal instead of proceeding
  if (paymentMethod === "UPI") {
    setShowUpiModal(true);
    return;
  }

  await proceedWithOrderPlacement(
    personalInfo,
    setPlacingOrder,
    navigate,
    orders,
    dispatch,
    setEmail
  );
};

export const proceedWithOrderPlacement = async (
  personalInfo,
  setPlacingOrder,
  navigate,
  orders
) => {
  setPlacingOrder(true);
  const customerName = `${personalInfo.firstName || ""} ${
    personalInfo.lastName || ""
  }`.trim();
  await updateCustomerInfoOrderApi(orders[0], customerName, personalInfo.email);
  const placeOrderApiResponse = await placeOrderApi(orders[0].ID);

  setPlacingOrder(false);
  
  // Check if payment link exists
  if (placeOrderApiResponse?.paymentLink) {
    const paymentLink = placeOrderApiResponse.paymentLink;

    // Keys-based Stripe returns the hosted Checkout URL — redirect in the same
    // tab (avoids popup blockers and keeps the return flow in one tab).
    if (paymentLink.includes("checkout.stripe.com")) {
      window.location.href = paymentLink;
      return;
    }

    // PayPal returns a hosted approval URL. Redirect in the same tab — the
    // buyer comes back through the API (which captures the payment) and is
    // then handed on to their order page, so a popup would strand that flow.
    if (paymentLink.includes("paypal.com")) {
      window.location.href = paymentLink;
      return;
    }

    // BACKUP — Stripe Connect returned a session id (cs_test_/cs_live_) and used
    // Stripe.js redirectToCheckout with the connected account. Re-enable when a
    // Connect license is available.
    if (paymentLink.startsWith('cs_test_') || paymentLink.startsWith('cs_live_')) {
      // This is a Stripe payment - redirect to Stripe Checkout
      const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_51LomnMSHpVQgjm8bENTP0UJybqU8fUSGH608DBrKOUeDmJaDglDRgXrpZ0ShWUKn7CSBkttHsNwKhoUExhmY3kGt00YWCVXoKi';
      const globalData = store.getState().globalData.globalDataName;
      const stripeAccountId = globalData?.stripeAccountId;
      
      if (window.Stripe) {
        // Initialize Stripe with the connected account ID
        const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : {};
        const stripe = window.Stripe(stripePublicKey, stripeOptions);
        
        // Redirect to Stripe Checkout
        stripe.redirectToCheckout({ sessionId: paymentLink })
          .then((result) => {
            if (result.error) {
              showToast(result.error.message, ToastTypes.ERROR);
              setPlacingOrder(false);
            }
          })
          .catch((error) => {
            console.error('Stripe redirect error:', error);
            showToast("Failed to redirect to payment. Please try again.", ToastTypes.ERROR);
            setPlacingOrder(false);
          });
        return;
      } else {
        showToast("Stripe is not loaded. Please refresh and try again.", ToastTypes.ERROR);
        setPlacingOrder(false);
        return;
      }
    }
    
    // Handle Razorpay or UPI payment links (regular URLs).
    // Always the same tab, on desktop too. Opening a new tab left the original
    // one sitting on the order page reading "Payment Pending" while the buyer
    // paid elsewhere, so a completed payment still looked unpaid — and the
    // gateway's return landed in the wrong tab. Matches Stripe and PayPal.
    window.location.href = paymentLink;
    return;
  }

  if (placeOrderApiResponse?.ID) {
    navigate(`/order-detail/${placeOrderApiResponse.uuid}`);
  }
};

export const handledPaymentMethodChanged = async (
  method,
  orders,
  setPaymentMethod,
  setOrders,
  personalInfo
) => {
  setPaymentMethod(method);
  const methodNumber = paymentMethodNumber(method);
  if (methodNumber === null) {
    console.error(`Unsupported payment method: ${method}`);
    showToast("This payment method isn't available right now.", ToastTypes.ERROR);
    return;
  }
  const customerName = `${personalInfo.firstName || ""} ${
    personalInfo.lastName || ""
  }`.trim();
  await updateOrderPaymentMethodApi(
    orders[0].ID,
    orders[0].customerId,
    customerName,
    null,
    methodNumber
  );
  await fetchAndSetOrders(setOrders);
  showToast("Payment method updated", ToastTypes.SUCCESS);
};

export const setDefaultPaymentOption = async (
  method,
  orders,
  setPaymentMethod,
  personalInfo
) => {
  setPaymentMethod(method);
  const methodNumber = paymentMethodNumber(method);
  if (methodNumber === null) {
    console.error(`Unsupported payment method: ${method}`);
    return;
  }
  const customerName = `${personalInfo.firstName || ""} ${
    personalInfo.lastName || ""
  }`.trim();
  await updateOrderPaymentMethodApi(
    orders[0].ID,
    orders[0].customerId,
    customerName,
    null,
    methodNumber
  );
};

export const handleCheckoutClicked = async (
  dispatch,
  setProcessing,
  onClose,
  navigate,
  cartItems,
  accessToken
) => {
  dispatch(setnavigateCheckout(true));
  setProcessing(true);
  if (!accessToken) {
    showToast(
      "You're almost there! Please log in to continue with checkout.",
      ToastTypes.ERROR
    );
    setProcessing(false);
    onClose();
    navigate("/login");
    return;
  }
  await checkoutAndAddLineItem(cartItems, dispatch, accessToken);
  setProcessing(false);
  onClose();
  navigate("/checkout");
};
